// @ts-check
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { fetchData } from '../mdc_modules/mdcApiFetch'
import {
  getActiveAccountNotifications,
  removeNotification,
  setNotificationAsSeen,
} from '../mdc_modules/notificationsApiRequests'
import { AuthContext } from './AuthContextProvider'

/** @typedef {{ notificationID: string | number, [key: string]: any }} NotificationItem */
/** @typedef {{ alerts: NotificationItem[], announcements: NotificationItem[], loading: boolean, hasNotifications: boolean, acknowledgeNotification: (notificationId: string | number) => void, removeNotification: (notificationId: string | number) => void }} NotificationContextValue */

/** @type {React.Context<NotificationContextValue>} */
export const NotificationContext = createContext(
  /** @type {NotificationContextValue} */ ({
    alerts: [],
    announcements: [],
    loading: false,
    hasNotifications: false,
    acknowledgeNotification: (/** @type {string | number} */ notificationId) => {},
    removeNotification: (/** @type {string | number} */ notificationId) => {},
  })
)

// TODO move this to server side settings so we can change on the fly
const NOTIFICATION_POLLING_INTERVAL_MS = 2 * 60 * 1000 // 2 mins

const EVENT_REFRESH_NOTIFICATIONS = 'mdxrefreshnotifications'

// this is a workaround introduced to allow the AccountNotification page
// to refresh notifications when they are acknowledged/removed from that view
// this is needed because the NotificationContext is not share between
// different roots created via ReactDOM.render(...)
/**
 * @returns {void}
 */
export const refreshNotifications = () => {
  document.dispatchEvent(new Event(EVENT_REFRESH_NOTIFICATIONS))
}

/** @typedef {{ children: React.ReactNode }} Props */

/**
 * @param {Props} props
 * @returns {React.ReactElement}
 */
function NotificationContextProvider({ children }) {
  const { authenticated } = useContext(AuthContext)
  // load initial state of notifications from auth context
  const [alerts, setAlerts] = useState(/** @type {NotificationItem[]} */ ([]))
  const [announcements, setAnnouncements] = useState(/** @type {NotificationItem[]} */ ([]))
  const [loading, setLoading] = useState(false)
  const [hasNotifications, setHasNotifications] = useState(false)

  /**
   * @param {string | number} notificationId
   * @returns {Promise<void>}
   */
  const handleAcknowledgeNotification = useCallback(
    async (/** @type {string | number} */ notificationId) => {
      const response = await fetchData(setNotificationAsSeen(String(notificationId)))
      if (response.success) {
        setAlerts((prevAlerts) =>
          prevAlerts.filter(
            (/** @type {NotificationItem} */ alert) => alert.notificationID !== notificationId
          )
        )
        setAnnouncements((prevAnnouncements) =>
          prevAnnouncements.filter(
            (/** @type {NotificationItem} */ announcement) =>
              announcement.notificationID !== notificationId
          )
        )
      }
    },
    []
  )
  /**
   * @param {string | number} notificationId
   * @returns {Promise<void>}
   */
  const handleRemoveNotification = useCallback(
    async (/** @type {string | number} */ notificationId) => {
      const response = await fetchData(removeNotification(String(notificationId)))
      if (response.success) {
        setAlerts((prevAlerts) =>
          prevAlerts.filter(
            (/** @type {NotificationItem} */ alert) => alert.notificationID !== notificationId
          )
        )
        setAnnouncements((prevAnnouncements) =>
          prevAnnouncements.filter(
            (/** @type {NotificationItem} */ announcement) =>
              announcement.notificationID !== notificationId
          )
        )
      }
    },
    []
  )

  /**
   * @returns {Promise<void>}
   */
  const handleFetchNotifications = async () => {
    if (!authenticated || loading) return
    setLoading(true)
    console.debug('polling notifications...')
    try {
      const response = await fetchData(getActiveAccountNotifications())
      if (response.success) {
        setAlerts(response.alerts)
        setAnnouncements(response.announcements)
      }
    } finally {
      setTimeout(() => setLoading(false), 50)
    }
  }

  /**
   * @returns {ReturnType<typeof setInterval>}
   */
  const initPolling = () => {
    handleFetchNotifications()
    // setInterval should be ok here since the polling interval is intended to be
    // much higher than the call latency. If this is not the case we should consider
    // returning a timeout handle with a recursive call
    return setInterval(() => {
      handleFetchNotifications()
    }, NOTIFICATION_POLLING_INTERVAL_MS)
  }

  useEffect(() => {
    setHasNotifications(alerts?.length > 0 || announcements?.length > 0)
  }, [alerts, announcements])

  useEffect(() => {
    /** @type {ReturnType<typeof setInterval> | undefined} */
    let interval = initPolling()
    /**
     * @returns {void}
     */
    const visibilityChangeListener = () => {
      // always clear interval on viz change
      interval && clearInterval(interval)

      // re-initialize when visible
      if (document.hidden) {
        console.debug('pausing notification polling')
      } else {
        console.debug('resuming notification polling')
        interval = initPolling()
      }
    }
    /**
     * @returns {void}
     */
    const refreshNotifications = () => {
      handleFetchNotifications()
    }
    document.addEventListener('visibilitychange', visibilityChangeListener)
    document.addEventListener(EVENT_REFRESH_NOTIFICATIONS, refreshNotifications)
    return () => {
      // clear interval and remove event listener to prevent memory leaks
      clearInterval(interval)
      document.removeEventListener('visibilitychange', visibilityChangeListener)
      document.removeEventListener(EVENT_REFRESH_NOTIFICATIONS, refreshNotifications)
    }
  }, [authenticated]) // depend on authenticated state since polling can only happen after authentication

  /** @type {NotificationContextValue} */
  const contextValue = {
    alerts,
    announcements,
    loading,
    hasNotifications,
    acknowledgeNotification: useCallback(
      (/** @type {string | number} */ id) => handleAcknowledgeNotification(id),
      [handleAcknowledgeNotification]
    ),
    removeNotification: useCallback(
      (/** @type {string | number} */ id) => handleRemoveNotification(id),
      [handleRemoveNotification]
    ),
  }

  return (
    <NotificationContext.Provider value={contextValue}>{children}</NotificationContext.Provider>
  )
}

export default NotificationContextProvider
